import type * as React from 'react';
import { cn } from '../../lib/utils.js';
import { Button } from '../ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.js';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet.js';

/**
 * FormDrawer — `<Sheet>` (side drawer) OR `<Dialog>` (centered modal)
 * wrapper for create/edit forms.
 *
 * Default variant is `'dialog'` (centered modal) — this matches the
 * client correction Punto 1 ("Las ventanas emergentes no se están
 * viendo centradas, se están viendo hacia un lado"). Wide forms
 * (the catalog Product / Model / Type panels) can opt back into
 * the side drawer with `variant="drawer"` for the explicit
 * landscape real-estate they need.
 *
 * Body layout is fixed structure (header / scrollable body / footer)
 * and exposes the form's submit + cancel handlers. Submitting is
 * expected to be controlled by the consumer — pass
 * `isSubmitting=true` while the API call is in flight and the modal
 * disables both buttons + changes the submit label.
 *
 * Wiring note: the inner `<form>` invokes `event.preventDefault()`
 * before calling the consumer's `onSubmit` so they receive a clean
 * native event without risking a full-page reload.
 */
export interface FormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  cancelLabel?: string;
  submitLabel?: string;
  isSubmitting?: boolean;
  /**
   * `'dialog'` (default) renders a centered Radix `Dialog`.
   * `'drawer'` renders a side `Sheet` — kept for forms that need
   * landscape real-estate (wide catalog entities).
   */
  variant?: 'dialog' | 'drawer';
  /** Drawer side when `variant='drawer'` (default right). Ignored in `dialog`. */
  side?: 'right' | 'left';
  /** Width override for `variant='dialog'`. Default `max-w-lg`. */
  contentClassName?: string;
  className?: string;
}

export function FormDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  onSubmit,
  cancelLabel = 'Cancelar',
  submitLabel = 'Guardar',
  isSubmitting = false,
  variant = 'dialog',
  side = 'right',
  contentClassName,
  className,
}: FormDrawerProps): React.ReactElement {
  const header = (
    <>
      <DialogHeader className={variant === 'dialog' ? undefined : 'sr-only'}>
        <DialogTitle>{title}</DialogTitle>
        {description ? <DialogDescription>{description}</DialogDescription> : null}
      </DialogHeader>
    </>
  );

  const body = (
    <div className={variant === 'dialog' ? 'flex-1 space-y-4' : 'flex-1 space-y-4 overflow-y-auto pr-1'}>
      {children}
    </div>
  );

  const footer = (
    <DialogFooter className={cn(variant === 'drawer' && 'mt-auto', 'gap-2')}>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={isSubmitting}
      >
        {cancelLabel}
      </Button>
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Guardando…' : submitLabel}
      </Button>
    </DialogFooter>
  );

  if (variant === 'drawer') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side={side}
          className={cn('flex flex-col', className, contentClassName)}
        >
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            {description ? <SheetDescription>{description}</SheetDescription> : null}
          </SheetHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit(event);
            }}
            // `min-h-0` is the standard flexbox fix for "the inner scroll
            // container won't shrink below its content" — without it, a
            // flex child defaults to `min-height: auto` (intrinsic size),
            // which means the body of a tall form keeps growing past the
            // viewport instead of clipping + scrolling. The inner
            // `overflow-y-auto` div then has nothing to scroll because
            // its parent already grew to fit everything.
            className="flex min-h-0 flex-1 flex-col gap-4"
          >
            {body}
            {footer}
          </form>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-h-[90vh] flex flex-col', contentClassName, className)}>
        {header}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(event);
          }}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1"
        >
          {body}
          {footer}
        </form>
      </DialogContent>
    </Dialog>
  );
}
